import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/Card';
import { Badge } from './ui/Badge';
import { Button } from './ui/Button';
import { ProgressRing } from './ui/ProgressRing';
import { QualificationRules, Rule } from './QualificationRules';
import { ChevronDownIcon, ChevronUpIcon, UsersIcon, PhoneIcon, PencilIcon } from 'lucide-react';
export interface Campaign {
  id: string;
  name: string;
  status: 'active' | 'paused' | 'completed';
  leadsCount: number;
  contactRate: number;
  healthScore: number;
  rules: Rule[];
}
interface CampaignCardProps {
  campaign: Campaign;
  onViewLeads: (id: string) => void;
  onEditRules?: (campaign: Campaign) => void;
  isSelected?: boolean;
}
export function CampaignCard({
  campaign,
  onViewLeads,
  onEditRules,
  isSelected
}: CampaignCardProps) {
  const [showRules, setShowRules] = useState(false);
  const healthColor = campaign.healthScore > 70 ? 'border-l-emerald-500' : campaign.healthScore > 40 ? 'border-l-amber-500' : 'border-l-red-500';
  const ringColor = campaign.healthScore > 70 ? 'text-emerald-500' : campaign.healthScore > 40 ? 'text-amber-500' : 'text-red-500';
  return <Card className={`transition-all duration-200 border-l-[3px] ${healthColor} ${isSelected ? 'ring-2 ring-blue-500 ring-offset-2 shadow-lg' : ''}`} data-id="element-1">
      <CardHeader className="flex flex-row items-center justify-between pb-3 border-b-0" data-id="element-2">
        <div className="flex items-center gap-4" data-id="element-3">
          <ProgressRing progress={campaign.healthScore} size={52} color={ringColor} data-id="element-4" />
          <div data-id="element-5">
            <CardTitle className="text-base font-bold" data-id="element-6">
              {campaign.name}
            </CardTitle>
            <div className="flex items-center gap-2 mt-1.5" data-id="element-7">
              <Badge variant={campaign.status === 'active' ? 'success' : 'neutral'} data-id="element-8">
                {campaign.status.charAt(0).toUpperCase() + campaign.status.slice(1)}
              </Badge>
              <span className="text-xs font-mono text-gray-400" data-id="element-9">
                #{campaign.id.split('-')[1]}
              </span>
            </div>
          </div>
        </div>
      </CardHeader>

      <CardContent className="pt-0 pb-5 px-5" data-id="element-10">
        <div className="grid grid-cols-2 gap-3 mb-5" data-id="element-11">
          <div className="bg-slate-50 p-3 rounded-lg border border-slate-100" data-id="element-12">
            <div className="flex items-center gap-2 text-slate-500 mb-1" data-id="element-13">
              <UsersIcon className="w-3.5 h-3.5" data-id="element-14" />
              <span className="text-xs font-medium uppercase tracking-wide" data-id="element-15">
                Leads
              </span>
            </div>
            <p className="text-xl font-bold text-slate-900" data-id="element-16">
              {campaign.leadsCount.toLocaleString()}
            </p>
          </div>
          <div className="bg-slate-50 p-3 rounded-lg border border-slate-100" data-id="element-17">
            <div className="flex items-center gap-2 text-slate-500 mb-1" data-id="element-18">
              <PhoneIcon className="w-3.5 h-3.5" data-id="element-19" />
              <span className="text-xs font-medium uppercase tracking-wide" data-id="element-20">
                Contact Rate
              </span>
            </div>
            <p className="text-xl font-bold text-slate-900" data-id="element-21">
              {campaign.contactRate}%
            </p>
          </div>
        </div>

        <div className="mb-5" data-id="element-22">
          <div className="flex items-center gap-2" data-id="element-23">
            <button onClick={() => setShowRules(!showRules)} className="flex-1 flex items-center justify-between px-3 py-2 text-xs font-medium text-gray-600 bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors border border-gray-100" data-id="element-24">
              <span data-id="element-25">Qualification Rules ({campaign.rules.length})</span>
              {showRules ? <ChevronUpIcon className="w-3.5 h-3.5 text-gray-500" data-id="element-26" /> : <ChevronDownIcon className="w-3.5 h-3.5 text-gray-500" data-id="element-27" />}
            </button>
            {onEditRules && <button onClick={e => {
            e.stopPropagation();
            onEditRules(campaign);
          }} className="p-2 text-gray-400 hover:text-blue-600 bg-gray-50 hover:bg-blue-50 rounded-lg border border-gray-100 transition-colors" title="Edit Rules" data-id="element-28">
                <PencilIcon className="w-4 h-4" data-id="element-29" />
              </button>}
          </div>

          {showRules && <div className="mt-3 animate-in fade-in slide-in-from-top-2 duration-200" data-id="element-30">
              <QualificationRules rules={campaign.rules} data-id="element-31" />
            </div>}
        </div>

        <Button variant={isSelected ? 'primary' : 'secondary'} className="w-full justify-between group" onClick={() => onViewLeads(campaign.id)} data-id="element-32">
          <span data-id="element-33">{isSelected ? 'Viewing Leads' : 'View Leads'}</span>
          <ChevronDownIcon className={`w-4 h-4 transition-transform duration-200 ${isSelected ? '-rotate-90' : '-rotate-90 opacity-0 group-hover:opacity-100 group-hover:translate-x-1'}`} data-id="element-34" />
        </Button>
      </CardContent>
    </Card>;
}